import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import saveAs from "file-saver";

export const EXPORT_SCALE = 2;
export const A4_LANDSCAPE_WIDTH = 1587;
export const A4_LANDSCAPE_HEIGHT = 1123;
export const A4_PADDING = 48;

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1
  );
}

export function canvasToBlob(canvas, type = "image/jpeg", quality = 1) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Unable to create image file"));
    }, type, quality);
  });
}

export function createPrintPage(sourceCanvas, orientation = "landscape") {
  const pageSize = getA4PageSize(orientation);
  const pageWidth = pageSize.width * EXPORT_SCALE;
  const pageHeight = pageSize.height * EXPORT_SCALE;
  const padding = A4_PADDING * EXPORT_SCALE;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  canvas.width = pageWidth;
  canvas.height = pageHeight;

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, pageWidth, pageHeight);

  const scale = Math.min(
    (pageWidth - padding * 2) / sourceCanvas.width,
    (pageHeight - padding * 2) / sourceCanvas.height
  );
  const targetWidth = sourceCanvas.width * scale;
  const targetHeight = sourceCanvas.height * scale;
  const targetX = (pageWidth - targetWidth) / 2;
  const targetY = (pageHeight - targetHeight) / 2;

  context.drawImage(sourceCanvas, targetX, targetY, targetWidth, targetHeight);
  return canvas;
}

export async function renderExportElementToCanvas(element, metadata, options = {}) {
  const sourceElement = element && element.$el ? element.$el : element;
  if (!sourceElement) throw new Error("Export element not found");

  const orientation = options.orientation || "landscape";
  const pageSize = getA4PageSize(orientation);
  const contentWidth = pageSize.width - A4_PADDING * 2;
  const contentMinHeight = pageSize.height - A4_PADDING * 2;
  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.left = "-10000px";
  wrapper.style.top = "0";
  wrapper.style.width = `${contentWidth}px`;
  wrapper.style.minHeight = `${contentMinHeight}px`;
  wrapper.style.padding = "0";
  wrapper.style.boxSizing = "border-box";
  wrapper.style.background = "#ffffff";
  wrapper.style.color = "#3f302a";
  wrapper.style.fontFamily = "Sarabun, Arial, sans-serif";

  wrapper.appendChild(createExportHeader(metadata));

  const content = sourceElement.cloneNode(true);
  content.style.width = "100%";
  content.style.maxWidth = "none";
  content.style.boxShadow = "none";
  content.style.margin = "0";
  if (content.matches(".schedule-panel") && content.querySelector(".exam-list")) {
    content.style.border = "none";
  }

  if (options.hideSelector) {
    content.querySelectorAll(options.hideSelector).forEach((node) => node.remove());
  }

  content.querySelectorAll(".v-tooltip__content").forEach((node) => node.remove());
  content.querySelectorAll(".class-block").forEach((node) => {
    node.style.boxShadow = "none";
    node.style.border = "1px solid rgba(122, 95, 81, 0.16)";
  });
  content.querySelectorAll(".class-block--clickable").forEach((node) => {
    node.style.boxShadow = "none";
    node.style.outline = "none";
  });
  content.querySelectorAll(".schedule-panel, .table-panel").forEach((node) => {
    node.style.boxShadow = "none";
  });
  content.querySelectorAll(".schedule-panel").forEach((node) => {
    if (node.querySelector(".exam-list")) {
      node.style.border = "none";
    }
  });
  content.querySelectorAll(".corner-cell, .time-cell, .day-cell, .subject-table th").forEach((node) => {
    node.style.fontFamily = "Sarabun, sans-serif";
  });

  wrapper.appendChild(content);
  document.body.appendChild(wrapper);

  try {
    await waitForAssets(wrapper);
    const renderHeight = Math.max(wrapper.scrollHeight, contentMinHeight);
    return await html2canvas(wrapper, {
      backgroundColor: "#ffffff",
      scale: EXPORT_SCALE,
      useCORS: true,
      allowTaint: false,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      windowWidth: contentWidth,
      windowHeight: renderHeight,
    });
  } finally {
    wrapper.remove();
  }
}

export function savePdfPages(pages, fileName) {
  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
    orientation: pages[0].orientation,
  });

  pages.forEach((page, index) => {
    if (index > 0) doc.addPage("a4", page.orientation);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.addImage(
      page.canvas.toDataURL("image/jpeg", 1),
      "JPEG",
      0,
      0,
      pageWidth,
      pageHeight
    );
  });

  doc.save(`${fileName}.pdf`);
}

export async function saveJpgPages(pages, fileName) {
  if (isIOS() && pages.length > 1) {
    const mergedCanvas = mergeCanvasesVertically(pages.map((page) => page.canvas));
    const blob = await canvasToBlob(mergedCanvas, "image/jpeg", 1);
    saveAs(blob, `${fileName}.jpg`);
    return;
  }

  for (let index = 0; index < pages.length; index += 1) {
    const blob = await canvasToBlob(pages[index].canvas, "image/jpeg", 1);
    saveAs(blob, `${fileName}-${pages[index].fileSuffix}.jpg`);
  }
}

function createExportHeader(metadata = {}) {
  const header = document.createElement("div");
  header.style.display = "grid";
  header.style.gridTemplateColumns = "128px minmax(0, 1fr)";
  header.style.gap = "24px";
  header.style.alignItems = "center";
  header.style.marginBottom = "28px";
  header.style.paddingBottom = "20px";
  header.style.borderBottom = "2px solid #f1e1d6";
  header.style.fontFamily = "Sarabun, sans-serif";

  const logo = document.createElement("img");
  logo.src = "kmitl_logo.png";
  logo.alt = "KMITL";
  logo.style.display = "block";
  logo.style.width = "auto";
  logo.style.height = "auto";
  logo.style.maxWidth = "120px";
  logo.style.maxHeight = "120px";
  logo.style.objectFit = "contain";
  header.appendChild(logo);

  const details = document.createElement("div");
  details.style.minWidth = "0";
  details.innerHTML = `
    <div style="font-size: 18px; font-weight: 700; color: #5d4036; line-height: 1.25; font-family: Anuphan, sans-serif;">
      ${escapeHtml(metadata.title || "")}
    </div>
    <div style="display: grid; grid-template-columns: minmax(0, 1fr); gap: 3px; margin-top: 8px; font-size: 14px; line-height: 1.2; font-family: Sarabun, sans-serif; text-align: left;">
      <div><strong>${escapeHtml(metadata.studentIdLabel || "Student ID")}:</strong> ${escapeHtml(metadata.studentId || "-")}</div>
      <div><strong>${escapeHtml(metadata.studentNameLabel || "Name")}:</strong> ${escapeHtml(metadata.studentName || "-")}</div>
      <div><strong>${escapeHtml(metadata.termLabel || "Term")}:</strong> ${escapeHtml(metadata.term || "-")}</div>
      <div><strong>${escapeHtml(metadata.updatedAtLabel || "Updated at")}:</strong> ${escapeHtml(metadata.updatedAt || "-")}</div>
    </div>
  `;
  header.appendChild(details);

  return header;
}

async function waitForAssets(root) {
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready;
  }

  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    images.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.onload = resolve;
        image.onerror = resolve;
      });
    })
  );
}

function getA4PageSize(orientation = "landscape") {
  if (orientation === "portrait") {
    return {
      width: A4_LANDSCAPE_HEIGHT,
      height: A4_LANDSCAPE_WIDTH,
    };
  }

  return {
    width: A4_LANDSCAPE_WIDTH,
    height: A4_LANDSCAPE_HEIGHT,
  };
}

function mergeCanvasesVertically(canvases) {
  const width = Math.max(...canvases.map((canvas) => canvas.width));
  const height = canvases.reduce((sum, canvas) => sum + canvas.height, 0);
  const mergedCanvas = document.createElement("canvas");
  const context = mergedCanvas.getContext("2d");
  let offsetY = 0;

  mergedCanvas.width = width;
  mergedCanvas.height = height;
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);

  canvases.forEach((canvas) => {
    const x = (width - canvas.width) / 2;
    context.drawImage(canvas, x, offsetY);
    offsetY += canvas.height;
  });

  return mergedCanvas;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
